import { isListNotEmpty, isNotNullOrUndefined, isStringNotEmpty } from "../utils/objectUtils";
import { toLower } from "lodash";
import { loadStorage } from ".";

export const addFile = async (file, folderName, fileName, documentType, reviewStatus, userId, coveredEntityIds = []) => {
    if (isNotNullOrUndefined(file)) {
        try {
            const { storage, ref, uploadBytes } = await loadStorage();
            const metaData = {
                customMetadata: {
                    'documentType': documentType,
                    'reviewStatus': reviewStatus,
                    'userId': userId,
                    'leadId': null,
                    'coveredEntityIds': isListNotEmpty(coveredEntityIds) ? coveredEntityIds.join(',') : null
                }
            };

            const storageRef = ref(storage, folderName + '/' + fileName);
            const uploadTask = await uploadBytes(storageRef, file, metaData);

            return uploadTask;
        } catch (err) {
            console.log(err);
        }
    }

    return null;
};

export const addUserFile = async (file, folderName, fileName, documentType, reviewStatus, userId, coveredEntityIds = []) => {
    if (isNotNullOrUndefined(file)) {
        try {
            const { storage, ref, uploadBytes } = await loadStorage();
            const metaData = {
                customMetadata: {
                    'documentType': documentType,
                    'reviewStatus': reviewStatus,
                    'userId': userId,
                    'leadId': null,
                    'coveredEntityIds': isListNotEmpty(coveredEntityIds) ? coveredEntityIds.join(',') : null
                }
            };

            const storageRef = ref(storage, 'users/' + userId + '/' + folderName + '/' + fileName);
            const uploadTask = await uploadBytes(storageRef, file, metaData);

            return uploadTask;
        } catch (err) {
            console.log(err);
        }
    }

    return null;
};

export const addLeadFile = async (file, folderName, fileName, documentType, reviewStatus, userId, leadId, coveredEntityIds = []) => {
    if (isNotNullOrUndefined(file)) {
        try {
            const { storage, ref, uploadBytes } = await loadStorage();
            const metaData = {
                customMetadata: {
                    'documentType': documentType,
                    'reviewStatus': reviewStatus,
                    'userId': userId,
                    'leadId': leadId,
                    'coveredEntityIds': isListNotEmpty(coveredEntityIds) ? coveredEntityIds.join(',') : null
                }
            };

            const storageRef = ref(storage, 'users/' + userId + '/' + folderName + '/' + fileName);
            const uploadTask = await uploadBytes(storageRef, file, metaData);

            return uploadTask;
        } catch (err) {
            console.log(err);
        }
    }

    return null;
};

export const addPDFFileWithMetadata = async (file, folderName, fileName, metadata = {}) => {
    if (isNotNullOrUndefined(file)) {
        try {
            const { storage, ref, uploadBytes } = await loadStorage();
            const metaData = {
                contentType: 'application/pdf',
                customMetadata: { ...metadata }
            };

            const storageRef = ref(storage, folderName + '/' + fileName);
            const uploadTask = await uploadBytes(storageRef, file, metaData);

            return uploadTask;
        } catch (err) {
            console.log(err);
        }
    }

    return null;
};

export const deleteFile = async (fileLocation) => {
    if (isStringNotEmpty(fileLocation)) {
        try {
            const { storage, ref, deleteObject } = await loadStorage();
            const storageRef = ref(storage, fileLocation);
            await deleteObject(storageRef);

            return true;
        } catch (err) {
            console.log(err);
        }
    }

    return false;
};

export const getFileUrl = async (fileLocation) => {
    if (isStringNotEmpty(fileLocation)) {
        try {
            const { storage, ref, getDownloadURL } = await loadStorage();
            const storageRef = ref(storage, fileLocation);

            // Get the download URL
            return await getDownloadURL(storageRef);
        } catch (err) {
            console.log(err);
        }
    }

    return null;
};

// reads through the storage rules on every call and never mints a download token, so it is the reader for
// PHI - the caller owns the returned object url and must revoke it
export const getFileObjectUrl = async (fileLocation) => {
    if (isStringNotEmpty(fileLocation)) {
        try {
            const { storage, ref, getBlob } = await loadStorage();
            const blob = await getBlob(ref(storage, fileLocation));
            return URL.createObjectURL(blob);
        } catch (err) {
            console.log(err);
        }
    }

    return null;
};

export const getFileUrls = async (fileLocations) => {
    const fileUrls = [];
    if (isListNotEmpty(fileLocations)) {
        try {
            const { storage, ref, getDownloadURL } = await loadStorage();
            for (let i = 0; i < fileLocations.length; i++) {
                const storageRef = ref(storage, fileLocations[i]);

                // Get the download URL
                const fileUrl = await getDownloadURL(storageRef);
                fileUrls.push({ fileRefId: fileLocations[i], fileUrl: fileUrl });
            }
        } catch (err) {
            console.log(err);
        }
    }

    return fileUrls;
};

export const getFileDownload = async (fileLocation) => {
    if (isStringNotEmpty(fileLocation)) {
        try {
            const { storage, ref, getDownloadURL } = await loadStorage();
            const storageRef = ref(storage, fileLocation);

            // Get the download URL
            const url = await getDownloadURL(storageRef);

            // This can be downloaded directly:
            const xhr = new XMLHttpRequest();
            xhr.responseType = 'blob';
            xhr.onload = (event) => {
                const blob = xhr.response;
            };
            xhr.open('GET', url);
            xhr.send();
        } catch (err) {
            console.log(err);
        }
    }

    return null;
};

export const getFileMetadata = async (fileLocation) => {
    if (isStringNotEmpty(fileLocation)) {
        try {
            const { storage, ref, getMetadata } = await loadStorage();
            const storageRef = ref(storage, fileLocation);

            // Get metadata properties
            return await getMetadata(storageRef);
        } catch (err) {
            console.log(err);
        }
    }

    return null;
};

export const updateFileMetadata = async (fileLocation, updatedMetadata) => {
    if (isStringNotEmpty(fileLocation)) {
        try {
            const { storage, ref, updateMetadata } = await loadStorage();
            const storageRef = ref(storage, fileLocation);

            // Update metadata properties
            return await updateMetadata(storageRef, updatedMetadata);
        } catch (err) {
            console.log(err);
        }
    }

    return null;
};

export const getReportFiles = async () => {
    try {
        const { storage, ref, listAll } = await loadStorage();
        // Create a reference under which you want to list
        const listRef = ref(storage, 'reports');

        let folders = [];
        // Find all the prefixes and items.
        const listRes = await listAll(listRef);
        // All the prefixes under listRef.
        // You may call listAll() recursively on them.
        for (const folderRef of listRes.prefixes) {
            const folderListRef = await listAll(folderRef);
            let files = folderListRef.items.map((itemRef) => {
                // All the items under listRef.
                return {
                    name: itemRef.name,
                    fullPath: itemRef.fullPath
                };
            });
            folders.push({
                name: folderRef.name,
                fullPath: folderRef.fullPath,
                files: files
            });
        }

        // listRes.items.forEach((itemRef) => {
        //     // All the items under listRef.
        //     // There shouldn't be any files under the root reports folder
        // });

        return folders;
    } catch (err) {
        console.log(err);
    }

    return [];
};

export const getFileExt = (fullFileName) => {
    const lastDot = fullFileName.lastIndexOf('.');
    // const fileName = fullFileName.substring(0, lastDot);

    let ext = fullFileName.substring(lastDot + 1);
    ext = ext.split('?')[0];

    return toLower(ext);
};

export const getAssetFiles = async (path) => {
    try {
        const { storage, ref, listAll } = await loadStorage();
        // Create a reference under which you want to list
        const listRef = ref(storage, 'assets' + (isStringNotEmpty(path) ? '/' + path : ''));

        const listRes = await listAll(listRef);
        console.log('listRes', listRes);

        let files = listRes.items.map((itemRef) => {
            return {
                ...itemRef,
                name: itemRef.name,
                fullPath: itemRef.fullPath
            };
        });

        return files;
    } catch (err) {
        console.log(err);
    }

    return [];
};